import React, { useState, useCallback, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import "react-datetime/css/react-datetime.css";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../../components/panel/panel.jsx";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import CategoryModal from "../../../components/CustomComponents/modal/parentCategoryModel.jsx";
import GridSectionLoader from "../../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../../config/axiosApi/axiosApi.jsx";
import APIs from "../../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery.jsx";
import FormInput from "../../../components/CustomComponents/Input/formInput.jsx";
import CommonTimeSlotForAllWeek from "../../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import ScheduleManager from "../../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import schedule from "../../../components/CustomComponents/TimeSchedule/schedule.js";
import CategoryIconSelection from "../categoryIcon.js";
import CategoryDepartmentsModal from "../../../components/CustomComponents/modal/rootCategoryGroup.jsx";
import ReportingCategoryModel from "../../../components/CustomComponents/modal/reportingCategory";
import { appSettings } from "../../../app/appSettingsSlice";
import SaveActions from "../../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../../hooks/useUnsavedChangesGuard.js";
import useCreateFormDirtyBaseline from "../../../hooks/useCreateFormDirtyBaseline.js";

function AddCategory() {
  const compID = useSelector((state) => state.auth.user).userData?.compID;
  const navigate = useNavigate();
  const { rootCatID } = useParams();
  const [categoryName, setCategoryName] = useState("");
  const [selectedParentCategory, setSelectedParentCategory] = useState({});
  const [selectedCategoryDepartment, setSelectedCategoryDepartment] = useState(null);
  const [selectedReportingCategory, setSelectedReportingCategory] = useState({});
  const [enableCategory, setEnableCategory] = useState(true);
  const [regularHours, setRegularHours] = useState(true);
  const [isAddOnCategory, setIsAddOnCategory] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [showParentCategoryModal, setShowParentCategoryModal] = useState(false);
  const [showCategoryDepartmentsModal, setShowCategoryDepartmentsModal] = useState(false);
  const [showReportingCategoryModal, setShowReportingCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [reportingCategories, setReportingCategories] = useState([]);
  const [platforms, setPlatforms] = useState({
    epos: true,
    kiosk: false,
    online: true,
    delivery: true,
  });
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    {
      from: "00:00",
      to: "23:59",
    },
  ]);
  const [schedules, setSchedules] = useState(schedule);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      categoryName: categoryName || "",
      selectedParentCategoryId: selectedParentCategory?.rootCat_ID || 0,
      selectedCategoryDepartmentId: selectedCategoryDepartment?.cD_ID || null,
      selectedReportingCategoryId: selectedReportingCategory?.rC_ID || null,
      enableCategory: Boolean(enableCategory),
      regularHours: Boolean(regularHours),
      isAddOnCategory: Boolean(isAddOnCategory),
      selectedIcon: selectedIcon || "",
      platforms,
      useSameTimeSlots: Boolean(useSameTimeSlots),
      commonTimeSlot,
      schedules,
    });
  }, [
    categoryName,
    selectedParentCategory,
    selectedCategoryDepartment,
    selectedReportingCategory,
    enableCategory,
    regularHours,
    isAddOnCategory,
    selectedIcon,
    platforms,
    useSameTimeSlots,
    commonTimeSlot,
    schedules,
  ]);

  const [hasUserEdited, setHasUserEdited] = useState(false);
  const markUserEdited = useCallback(() => {
    setHasUserEdited((prev) => prev || true);
  }, []);

  const { isDirty, discardBaseline, syncBaselineAfterSave } =
    useCreateFormDirtyBaseline(getFormSnapshot, !hasUserEdited);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();


  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  const handleSave = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!categoryName.trim()) {
      showErrorToast("Category name is required");
      return false;
    }
    if (!selectedCategoryDepartment) {
      showErrorToast("Category department is required");
      return false;
    }

    setLoading(true);

    try {
      const formattedSchedules = [];
      if (!regularHours) {
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          days.forEach((day) => {
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                formattedSchedules.push({
                  ID: 0,
                  CompID: compID,
                  rootCat_ID: 0,
                  DayName: day,
                  OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                  CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                  UpdateDateTime: new Date().toISOString(),
                });
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A"]).isValid() &&
                  moment(slot.to, ["h:mm A"]).isValid()
                ) {
                  formattedSchedules.push({
                    ID: 0,
                    CompID: compID,
                    rootCat_ID: 0,
                    DayName: schedule.day,
                    OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                    CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                    UpdateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
      }

      const payload = {
        rootCat_ID: 0,
        localizeID: "en",
        compID,
        rootCat_Name: categoryName,
        rootCat_Detail: "",
        rootCat_Image: selectedIcon || "",
        rootCat_Publish: enableCategory,
        rootCat_Orderby: 0,
        rG_ID: 1,
        rootCat_IsSubCategory: selectedParentCategory?.rootCat_ID ? true : false,
        rootCat_ParentRootCat_ID: selectedParentCategory?.rootCat_ID || 0,
        rootCat_ColorCode: "",
        pCategoryName: selectedParentCategory?.rootCat_Name || "",
        updateDateTime: new Date().toISOString(),
        isActiveforTill: platforms.epos,
        isActiveforKIOSK: platforms.kiosk,
        isActiveforTakeAway: platforms.online,
        isActiveforDelivery: platforms.delivery,
        isAvailableInTimeIntervals: !regularHours, // Same as UpdateCategory: true when not regular hours
        isAddOnCategory: Boolean(isAddOnCategory),
        products: 0,
        subCategories: 0,
        subCategoriesList: [],
        rootCategoryWeeklyTimings: formattedSchedules,
        categoryDepartments: selectedCategoryDepartment ? [{
          cD_ID: selectedCategoryDepartment.cD_ID,
          cD_Name: selectedCategoryDepartment.cD_Name,
          compID: compID,
        }] : [],
        CategoryDepartmentRelations: selectedCategoryDepartment ? [{
          rootCat_ID: 0,
          cD_ID: selectedCategoryDepartment.cD_ID,
          compID: compID,
        }] : [],
        rootCat_Type: selectedReportingCategory?.rC_ID || 1, // Use rC_ID as RootCat_Type, default to 1 (Food)
      };

      const response = await axiosInstance.post(APIs.ADD_CATEGORY, payload);
      if (response.data.status) {
        showSuccessToast("Category added successfully");
        syncBaselineAfterSave();
        setHasUserEdited(false);
        if (exitAfterSave) {
          navigate("/categories/manageCategories");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to add category");
        return false;
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          "An error occurred while adding the category"
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const applyReportingAndDepartmentFromDetails = useCallback(
    (categoryDetails, reportingCategoriesList) => {
      if (categoryDetails?.categoryDepartments?.[0]) {
        setSelectedCategoryDepartment(categoryDetails.categoryDepartments[0]);
      } else {
        setSelectedCategoryDepartment(null);
      }
      if (categoryDetails?.rootCat_Type && reportingCategoriesList?.length > 0) {
        const foundReportingCategory = reportingCategoriesList.find(
          (rc) => rc.rC_ID === categoryDetails.rootCat_Type
        );
        setSelectedReportingCategory(foundReportingCategory || {});
      } else {
        setSelectedReportingCategory({});
      }
    },
    []
  );

  const handleParentCategorySelection = (selectedCategory) => {
    markUserEdited();
    if (
      selectedCategory?.id === "none" ||
      !selectedCategory?.rootCat_ID
    ) {
      setSelectedParentCategory({});
      setSelectedCategoryDepartment(null);
      setSelectedReportingCategory({});
    } else {
      setSelectedParentCategory(selectedCategory);
    }
    setShowParentCategoryModal(false);
  };

  const handleCategoryDepartmentsSelection = (selectedDepartment) => {
    markUserEdited();
    setSelectedCategoryDepartment(selectedDepartment);
    setShowCategoryDepartmentsModal(false);
  };

  const handleReportingCategorySelection = (selectedCategory) => {
    markUserEdited();
    setSelectedReportingCategory(selectedCategory);
    setShowReportingCategoryModal(false);
  };

  const handlePlatformChange = (platform) => {
    markUserEdited();
    setPlatforms({
      ...platforms,
      [platform]: !platforms[platform],
    });
  };

  const fetchCategories = useCallback(async (options = {}) => {
    const showGlobalLoading = options.showGlobalLoading !== false;
    const skipParentAutoSelect = options.skipParentAutoSelect === true;
    if (!compID) {
      return;
    }

    try {
      if (showGlobalLoading) setCategoriesLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          compID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });
      const categoryData = response.data.data || response.data;

      if (Array.isArray(categoryData)) {
        setCategories(categoryData);
      } else {
        setCategories([]);
      }
      if (!skipParentAutoSelect && Array.isArray(categoryData)) {
        const parentCategory = categoryData.find(
          (e) => e.rootCat_ID == rootCatID
        );
        if (parentCategory) {
          handleParentCategorySelection(parentCategory);
        }
      }
    } catch (err) {
      setCategories([]);
    } finally {
      if (showGlobalLoading) {
        setCategoriesLoading(false);
        setLoading(false);
      }
    }
  }, [compID, rootCatID]);

  const fetchReportingCategories = useCallback(async () => {
    if (!compID) {
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_TYPES, {
        params: {
          compID: compID,
        },
      });
      const categoryData = response.data.data || response.data;
      const reportingCategoriesData = Array.isArray(categoryData) ? categoryData : [];
      setReportingCategories(reportingCategoriesData);
      return reportingCategoriesData;
    } catch (err) {
      setReportingCategories([]);
      return [];
    }
  }, [compID]);

  useEffect(() => {
    fetchCategories();
    fetchReportingCategories();
  }, [fetchCategories, fetchReportingCategories]);

  useEffect(() => {
    const parentId = selectedParentCategory?.rootCat_ID;
    if (!parentId || !compID) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance.get(APIs.GET_CATEGORY_ROOT_ID, {
          params: { compID, RootCat_ID: parentId },
        });
        if (cancelled) {
          return;
        }
        const details = response.data.data || response.data;
        applyReportingAndDepartmentFromDetails(details, reportingCategories);
      } catch {
        if (!cancelled) {
          showErrorToast("Failed to load parent category settings");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedParentCategory?.rootCat_ID,
    compID,
    reportingCategories,
    applyReportingAndDepartmentFromDetails,
  ]);

  useEffect(() => {
    if (selectedParentCategory?.rootCat_ID || rootCatID) {
      return;
    }
    if (reportingCategories.length > 0 && !selectedReportingCategory?.rC_ID) {
      setSelectedReportingCategory(reportingCategories[0]);
    }
  }, [
    reportingCategories,
    selectedParentCategory?.rootCat_ID,
    rootCatID,
    selectedReportingCategory?.rC_ID,
  ]);

  const fieldsLockedFromParent = Boolean(selectedParentCategory?.rootCat_ID);

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSave({ exitAfterSave: false }),
    onDiscard: () => {
      discardBaseline();
      setHasUserEdited(false);
    },
  });

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/categories/manageCategories">Manage Categories</Link>
            </li>
            <li className="breadcrumb-item active">Add Category</li>
          </ol>
          <h1 className="page-header mb-0">Add Category</h1>
        </div>

        <SaveActions
          onCancel={() => navigate("/categories/manageCategories")}
          onSave={() => handleSave({ exitAfterSave: false })}
          onSaveAndExit={() => handleSave({ exitAfterSave: true })}
          saving={loading}
        />
      </div>
      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <div
              className="p-4 rounded-3 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                border: isDarkMode ? "1px solid #404040" : undefined,
                color: isDarkMode ? "#ffffff" : undefined,
              }}
            >
              {/* Top Section: Icon Selection and Toggle Switches */}
              <div className="row mb-4 align-items-center">
                <div className="col-md-3 text-center">
                  <CategoryIconSelection
                    selectedIcon={selectedIcon}
                    setSelectedIcon={(icon) => {
                      markUserEdited();
                      setSelectedIcon(icon);
                    }}
                  />
                </div>
                <div className="col-md-9">
                  <div className="row row-cols-2 g-3">
                    {[
                      { label: "Enable Category", field: "enableCategory", checked: enableCategory, toggle: () => { markUserEdited(); setEnableCategory(!enableCategory); } },
                      { label: "KIOSK", field: "kiosk", checked: platforms.kiosk, toggle: () => handlePlatformChange("kiosk") },
                      { label: "Online", field: "online", checked: platforms.online, toggle: () => handlePlatformChange("online") },
                      // { label: "Delivery", field: "delivery", checked: platforms.delivery, toggle: () => handlePlatformChange("delivery") },
                      { label: "E POS", field: "epos", checked: platforms.epos, toggle: () => handlePlatformChange("epos") },
                      { label: "Modifier Category", field: "isAddOnCategory", checked: isAddOnCategory, toggle: () => { markUserEdited(); setIsAddOnCategory(!isAddOnCategory); } },
                    ].map(({ label, field, checked, toggle }) => (
                      <div key={field} className="col">
                        <ToggleSwitch
                          isChecked={checked}
                          onToggle={toggle}
                          label={label}
                          name={field}
                          themeColor={themeColor}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Fields Section */}
              <div className="row g-4">
                <div className="col-md-6">
                  <FormInput
                    label="Category Name *"
                    value={categoryName}
                    onchange={(e) => {
                      markUserEdited();
                      setCategoryName(e.target.value);
                    }}
                    type="text"
                    placeHolder="Enter Category Name"
                    name="categoryName"
                    required
                  />
                </div>

                <div className="col-md-6">
                  <div className="position-relative">
                    <label 
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                    >
                      Parent Category
                    </label>
                    <div
                      className="form-control d-flex align-items-center justify-content-between cursor-pointer"
                      style={{
                        minHeight: "48px",
                        cursor: "pointer",
                        borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                        backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                        color: isDarkMode ? '#e2e8f0' : '#495057'
                      }}
                      onClick={() => setShowParentCategoryModal(true)}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {selectedParentCategory?.rootCat_Name ? (
                          <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                            {selectedParentCategory.rootCat_Name}
                          </span>
                        ) : (
                          <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                            Click to select parent category
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {selectedParentCategory?.rootCat_Name && (
                          <button
                            className="btn btn-link p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              markUserEdited();
                              setSelectedParentCategory({});
                              setSelectedCategoryDepartment(null);
                              setSelectedReportingCategory({});
                            }}
                            style={{ 
                              fontSize: "16px",
                              color: isDarkMode ? '#a0aec0' : '#6c757d'
                            }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                        <i
                          className={`fa ${
                            showParentCategoryModal
                              ? "fa-chevron-up"
                              : "fa-chevron-down"
                          }`}
                          style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="position-relative">
                    <label 
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                    >
                      Reporting Category Type
                    </label>
                    <div
                      className="form-control d-flex align-items-center justify-content-between"
                      style={{
                        minHeight: "48px",
                        cursor: fieldsLockedFromParent ? "not-allowed" : "pointer",
                        borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                        backgroundColor: fieldsLockedFromParent
                          ? (isDarkMode ? '#343a40' : '#f8f9fa')
                          : (isDarkMode ? '#2d3436' : '#ffffff'),
                        color: isDarkMode ? '#e2e8f0' : '#495057',
                        opacity: fieldsLockedFromParent ? 0.9 : 1,
                      }}
                      onClick={() => {
                        if (!fieldsLockedFromParent) {
                          setShowReportingCategoryModal(true);
                        }
                      }}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {selectedReportingCategory?.rC_Name ? (
                          <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                            {selectedReportingCategory.rC_Name}
                          </span>
                        ) : (
                          <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                            {fieldsLockedFromParent
                              ? "Inherited from parent category"
                              : "Click to select category type"}
                          </span>
                        )}
                      </div>
                      {!fieldsLockedFromParent && (
                        <div className="d-flex align-items-center gap-2">
                          {selectedReportingCategory?.rC_ID && (
                            <button
                              className="btn btn-link p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedReportingCategory({});
                              }}
                              style={{
                                fontSize: "16px",
                                color: isDarkMode ? '#a0aec0' : '#6c757d'
                              }}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          )}
                          <i
                            className={`fa ${
                              showReportingCategoryModal
                                ? "fa-chevron-up"
                                : "fa-chevron-down"
                            }`}
                            style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                          ></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="position-relative">
                    <label 
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}
                    >
                      Category Department
                    </label>
                    <div
                      className="form-control d-flex align-items-center justify-content-between"
                      style={{
                        minHeight: "48px",
                        cursor: fieldsLockedFromParent ? "not-allowed" : "pointer",
                        borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                        backgroundColor: fieldsLockedFromParent
                          ? (isDarkMode ? '#343a40' : '#f8f9fa')
                          : (isDarkMode ? '#2d3436' : '#ffffff'),
                        color: isDarkMode ? '#e2e8f0' : '#495057',
                        opacity: fieldsLockedFromParent ? 0.9 : 1,
                      }}
                      onClick={() => {
                        if (!fieldsLockedFromParent) {
                          setShowCategoryDepartmentsModal(true);
                        }
                      }}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        {selectedCategoryDepartment ? (
                          <span style={{ color: isDarkMode ? '#e2e8f0' : '#212529' }}>
                            {selectedCategoryDepartment.cD_Name}
                          </span>
                        ) : (
                          <span style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                            {fieldsLockedFromParent
                              ? "Inherited from parent category"
                              : "Click to select department"}
                          </span>
                        )}
                      </div>
                      {!fieldsLockedFromParent && (
                        <div className="d-flex align-items-center gap-2">
                          {selectedCategoryDepartment && (
                            <button
                              className="btn btn-link p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCategoryDepartment(null);
                              }}
                              style={{
                                fontSize: "16px",
                                color: isDarkMode ? '#a0aec0' : '#6c757d'
                              }}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          )}
                          <i
                            className={`fa ${
                              showCategoryDepartmentsModal
                                ? "fa-chevron-up"
                                : "fa-chevron-down"
                            }`}
                            style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                          ></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Scheduling Section */}
              <div className="row g-4 mt-2">
                <div className="col-12">
                  <div
                    className="card p-3"
                    style={{
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <h5 className="card-title mb-3">Time Scheduling</h5>
                    <div className="row g-3 d-flex align-items-center p-4">
                      <div className="col-6">
                        <ToggleSwitch
                          isChecked={regularHours}
                          onToggle={() => {
                            markUserEdited();
                            setRegularHours(!regularHours);
                          }}
                          label="Available in regular Business Hours"
                          name="regularHours"
                          themeColor={themeColor}
                        />
                      </div>
                    </div>

                    {!regularHours && (
                      <>
                        <div className="row g-3 d-flex align-items-center p-4">
                          <div className="col-6">
                            <ToggleSwitch
                              isChecked={useSameTimeSlots}
                              onToggle={() => {
                                markUserEdited();
                                setUseSameTimeSlots(!useSameTimeSlots);
                                if (!useSameTimeSlots) {
                                  applyCommonTimeSlotToAll();
                                }
                              }}
                              label="Use same time slots for all days"
                              name="useSameTimeSlots"
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                        <hr />

                        {useSameTimeSlots ? (
                          <CommonTimeSlotForAllWeek
                            setCommonTimeSlots={(slots) => {
                              markUserEdited();
                              setCommonTimeSlot(slots);
                            }}
                            commonTimeSlots={commonTimeSlot}
                          />
                        ) : (
                          <ScheduleManager
                            schedules={schedules}
                            setSchedules={(next) => {
                              markUserEdited();
                              setSchedules(next);
                            }}
                             isDarkMode={isDarkMode}
                            themeColor={themeColor}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <CategoryModal
              showParentCategoryModal={showParentCategoryModal}
              setShowParentCategoryModal={setShowParentCategoryModal}
              showButton={true}
              setSelectedParentCategory={setSelectedParentCategory}
              selectedParentCategory={selectedParentCategory}
              onSubmit={handleParentCategorySelection}
              categories={categories}
              categoriesLoading={categoriesLoading}
              fetchCategories={fetchCategories}
            />
            {!fieldsLockedFromParent && (
              <CategoryDepartmentsModal
                showModal={showCategoryDepartmentsModal}
                setShowModal={setShowCategoryDepartmentsModal}
                onSubmit={handleCategoryDepartmentsSelection}
                selectedGroups={selectedCategoryDepartment}
                setSelectedGroups={setSelectedCategoryDepartment}
                subtitle="Select category department for this category"
              />
            )}
            {!fieldsLockedFromParent && (
              <ReportingCategoryModel
                isModalOpen={showReportingCategoryModal}
                setIsModalOpen={setShowReportingCategoryModal}
                onCategorySubmit={handleReportingCategorySelection}
                compID={compID}
                selectedCategory={selectedReportingCategory}
                categories={reportingCategories}
                showButton={true}
                onRefresh={fetchReportingCategories}
              />
            )}
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>
    </div>
  );
}

export default AddCategory;